import { useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Button,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Spacer,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { NavLink as RouterNavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmailVerificationBanner } from '@/components/ui/EmailVerificationBanner';
import { useAuth } from '../features/auth/AuthProvider';
import { SubscriptionStatusBanner } from '../features/subscription/components/SubscriptionStatusBanner';
import { AudioPreviewProvider } from '../features/media-library/components/AudioPreview';

interface NavItemProps {
  to: string;
  label: string;
  end?: boolean;
  onNavigate?: () => void;
}

function NavItem({ to, label, end, onNavigate }: NavItemProps) {
  return (
    <Box
      as={RouterNavLink}
      to={to}
      end={end}
      onClick={onNavigate}
      display="block"
      px={3}
      py={2}
      borderRadius="md"
      fontSize="sm"
      fontWeight="500"
      color="gray.600"
      _hover={{ bg: 'gray.100', color: 'gray.900', textDecoration: 'none' }}
      _activeLink={{ bg: 'brand.50', color: 'brand.700', fontWeight: '600' }}
    >
      {label}
    </Box>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text px={3} pt={4} pb={1} fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">
      {children}
    </Text>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <VStack align="stretch" spacing={0.5} p={3} h="100%">
      <Text px={3} py={3} fontWeight="800" fontSize="lg" color="brand.600">
        Streamko
      </Text>
      <NavItem to="/app" label={t('nav.dashboard')} end onNavigate={onNavigate} />

      <SectionLabel>{t('nav.sections.content')}</SectionLabel>
      <NavItem to="/library" label={t('nav.library')} onNavigate={onNavigate} />
      <NavItem to="/scenarios" label={t('nav.scenarios')} onNavigate={onNavigate} />
      <NavItem to="/schedule" label={t('nav.schedule')} onNavigate={onNavigate} />
      <NavItem to="/player" label={t('nav.player')} onNavigate={onNavigate} />

      <SectionLabel>{t('nav.sections.organization')}</SectionLabel>
      <NavItem to="/organization/locations" label={t('nav.locations')} onNavigate={onNavigate} />
      <NavItem to="/alerts" label={t('nav.alerts')} onNavigate={onNavigate} />
      <NavItem to="/organization/users" label={t('nav.team')} onNavigate={onNavigate} />
      <NavItem to="/organization/settings" label={t('nav.organization')} onNavigate={onNavigate} />
      <NavItem to="/organization/audit-log" label={t('nav.auditLog')} onNavigate={onNavigate} />

      <SectionLabel>{t('nav.sections.account')}</SectionLabel>
      <NavItem to="/settings" label={t('nav.settings')} onNavigate={onNavigate} />
      <NavItem to="/subscription" label={t('nav.subscription')} onNavigate={onNavigate} />
    </VStack>
  );
}

function TopBar({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const { t } = useTranslation();
  const { authUser, signOut } = useAuth();
  return (
    <Flex
      as="header"
      align="center"
      h="56px"
      px={{ base: 3, md: 6 }}
      borderBottomWidth="1px"
      borderColor="gray.200"
      bg="white"
    >
      <IconButton
        aria-label={t('nav.openMenu')}
        icon={<HamburgerIcon />}
        variant="ghost"
        display={{ base: 'inline-flex', md: 'none' }}
        onClick={onOpenSidebar}
        mr={2}
      />
      <Spacer />
      <Menu>
        <MenuButton as={Button} variant="ghost" size="sm" rightIcon={<ChevronDownIcon />}>
          <HStack spacing={2}>
            <Avatar size="xs" name={authUser?.fullName || authUser?.email} />
            <Text display={{ base: 'none', sm: 'block' }} maxW="200px" isTruncated>
              {authUser?.email}
            </Text>
          </HStack>
        </MenuButton>
        <MenuList>
          <MenuItem onClick={() => void signOut()}>{t('nav.signOut')}</MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}

/**
 * Authenticated app shell: persistent sidebar (Drawer on mobile) + top bar, with
 * the subscription enforcement banner and the email-verification banner pinned
 * above the content. Replaces the old "URL-only islands" layout — every
 * authenticated page now renders inside this chrome.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { authUser, signInWithOidc } = useAuth();
  const [showVerify, setShowVerify] = useState(false);

  useEffect(() => {
    if (!authUser) {
      setShowVerify(false);
      return;
    }
    const dismissed = sessionStorage.getItem('verification_banner_dismissed') === 'true';
    setShowVerify(!authUser.emailVerified && !dismissed);
  }, [authUser]);

  const handleResend = useCallback(async () => {
    // Email-verification resend is owned by Zitadel; email-OTP accounts are
    // already verified so this only fires for OIDC users.
    await signInWithOidc();
  }, [signInWithOidc]);

  const handleDismiss = useCallback(() => {
    setShowVerify(false);
    sessionStorage.setItem('verification_banner_dismissed', 'true');
  }, []);

  return (
    <AudioPreviewProvider>
    <Flex direction="column" minH="100vh" bg="gray.50">
      <SubscriptionStatusBanner onManage={() => navigate('/subscription')} />
      {showVerify && authUser && !authUser.emailVerified && (
        <EmailVerificationBanner
          email={authUser.email}
          onResend={handleResend}
          onDismiss={handleDismiss}
        />
      )}

      <Flex flex={1} align="stretch">
        <Box
          as="nav"
          display={{ base: 'none', md: 'block' }}
          w="240px"
          flexShrink={0}
          borderRightWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          <SidebarContent />
        </Box>

        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent maxW="260px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <SidebarContent onNavigate={onClose} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Flex direction="column" flex={1} minW={0}>
          <TopBar onOpenSidebar={onOpen} />
          <Box as="main" flex={1} p={{ base: 4, md: 6 }} w="100%">
            <Box maxW="1200px" mx="auto" w="100%">
              {children}
            </Box>
          </Box>
        </Flex>
      </Flex>
    </Flex>
    </AudioPreviewProvider>
  );
}

export default AppLayout;
